import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    fetchPatientVirtualAppointments,
    fetchVirtualAppointmentRound,
    fetchVirtualAppointmentTypes,
    type VirtualAppointmentHandoffDto,
    type VirtualAppointmentListFilter
} from '../services/virtualAppointments';
import {
    PATIENT_VIRTUAL_APPOINTMENT_ROUND_KEY,
    PATIENT_VIRTUAL_APPOINTMENTS_KEY,
    PATIENT_VIRTUAL_APPOINTMENTS_LIST_KEY,
    VIRTUAL_APPOINTMENT_TYPES_KEY
} from './queryKeys';

const TYPES_STALE_MS = 30 * 60 * 1000;
const NO_HANDOFFS: readonly VirtualAppointmentHandoffDto[] = [];

interface PatientVirtualAppointmentsOptions {
    enabled?: boolean;
    /** Always refetch on mount, e.g. before editing, where a cached list could be stale. */
    fresh?: boolean;
}

export function usePatientVirtualAppointments(
    patientId: string,
    filter: VirtualAppointmentListFilter = {},
    { enabled = true, fresh = false }: PatientVirtualAppointmentsOptions = {}
) {
    const { groupId, status } = filter;
    return useQuery({
        queryKey: PATIENT_VIRTUAL_APPOINTMENTS_LIST_KEY(patientId, groupId ?? null, status ?? null),
        queryFn: () => fetchPatientVirtualAppointments(patientId, { groupId, status }),
        enabled: enabled && patientId.length > 0,
        ...(fresh && { staleTime: 0, refetchOnMount: 'always' as const })
    });
}

export interface VirtualAppointmentRound {
    /** Start of the case's current VA episode; null outside one, undefined until loaded. */
    startedAt: string | null | undefined;
    /** Oldest first; empty until loaded, too. */
    handoffs: readonly VirtualAppointmentHandoffDto[];
    failed: boolean;
    retry: () => void;
}

export function useVirtualAppointmentRound(patientId: string): VirtualAppointmentRound {
    const { data, isError, isFetching, refetch } = useQuery({
        queryKey: PATIENT_VIRTUAL_APPOINTMENT_ROUND_KEY(patientId),
        queryFn: () => fetchVirtualAppointmentRound(patientId),
        enabled: patientId.length > 0
    });
    const retry = useCallback(() => void refetch(), [refetch]);
    return {
        startedAt: data?.startedAt,
        handoffs: data?.handoffs ?? NO_HANDOFFS,
        failed: isError && !isFetching,
        retry
    };
}

export function useVirtualAppointmentTypes() {
    return useQuery({
        queryKey: VIRTUAL_APPOINTMENT_TYPES_KEY,
        queryFn: fetchVirtualAppointmentTypes,
        staleTime: TYPES_STALE_MS
    });
}

// Code -> descriptive label from the same cached query the type dropdown uses.
export function useVirtualAppointmentTypeLabels(): ReadonlyMap<string, string> {
    const { data } = useVirtualAppointmentTypes();
    return useMemo(() => new Map((data ?? []).map((t) => [t.code, t.label])), [data]);
}

export function useInvalidatePatientVirtualAppointments(patientId: string): () => void {
    const queryClient = useQueryClient();
    return useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: PATIENT_VIRTUAL_APPOINTMENTS_KEY(patientId)
        });
    }, [queryClient, patientId]);
}
